/**
 * DeployControlPage — 部署控制面板
 *
 * Frontend for the deployment control plane backend (deploy-control-plane.ts).
 * Manages: Groups, Instances, Templates, Runs.
 * Requires: platform_admin role.
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  Server,
  Layers,
  FileCode,
  Play,
  RefreshCw,
  Loader2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  Plus,
  Trash2,
  Heart,
  Edit,
  X,
} from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────

interface Group {
  id: string;
  slug: string;
  name: string;
  description?: string;
  region?: string;
  domain_pattern?: string;
  max_tenants?: number;
  tenant_count?: number;
  created_at: string;
}

interface Instance {
  id: string;
  slug: string;
  name: string;
  group_id: string;
  status: string;
  domain?: string;
  railway_project_id?: string;
  health_checked_at?: string;
  created_at: string;
}

interface Template {
  id: string;
  slug: string;
  name: string;
  description?: string;
  version: number;
  is_active: boolean;
  created_at: string;
}

interface Run {
  id: string;
  instance_id: string;
  action: string;
  status: string;
  started_at: string;
  finished_at?: string;
  error_message?: string;
  created_at: string;
}

type Tab = 'groups' | 'instances' | 'templates' | 'runs';

// ─── Helpers ──────────────────────────────────────────────────────

const apiFetch = async <T,>(url: string): Promise<T | null> => {
  try {
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) return null;
    const body = await res.json();
    return (body?.data ?? body) as T;
  } catch {
    return null;
  }
};

async function apiPost<T>(url: string, body: unknown): Promise<{ ok: boolean; data?: T; error?: string }> {
  try {
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const json = await res.json();
    if (!res.ok) return { ok: false, error: json?.error?.message ?? json?.message ?? `Status ${res.status}` };
    return { ok: true, data: (json?.data ?? json) as T };
  } catch (e) {
    return { ok: false, error: String(e) };
  }
}

async function apiPatch<T>(url: string, body: unknown): Promise<{ ok: boolean; data?: T; error?: string }> {
  try {
    const res = await fetch(url, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const json = await res.json();
    if (!res.ok) return { ok: false, error: json?.error?.message ?? json?.message ?? `Status ${res.status}` };
    return { ok: true, data: (json?.data ?? json) as T };
  } catch (e) {
    return { ok: false, error: String(e) };
  }
}

async function apiDelete(url: string): Promise<{ ok: boolean; error?: string }> {
  try {
    const res = await fetch(url, { method: 'DELETE', credentials: 'include' });
    if (!res.ok) {
      const json = await res.json().catch(() => ({}));
      return { ok: false, error: (json as any)?.error?.message ?? `Status ${res.status}` };
    }
    return { ok: true };
  } catch (e) {
    return { ok: false, error: String(e) };
  }
}

const STATUS_ICONS: Record<string, React.ReactNode> = {
  healthy:    <CheckCircle className="w-4 h-4 text-green-500" />,
  success:    <CheckCircle className="w-4 h-4 text-green-500" />,
  running:    <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />,
  pending:    <Clock className="w-4 h-4 text-yellow-500" />,
  provisioning: <Clock className="w-4 h-4 text-yellow-500" />,
  degraded:   <AlertTriangle className="w-4 h-4 text-orange-500" />,
  unhealthy:  <XCircle className="w-4 h-4 text-red-500" />,
  failed:     <XCircle className="w-4 h-4 text-red-500" />,
  stopped:    <XCircle className="w-4 h-4 text-gray-400" />,
  rolled_back:<AlertTriangle className="w-4 h-4 text-orange-500" />,
  decommissioned: <XCircle className="w-4 h-4 text-gray-400" />,
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span className="inline-flex items-center gap-1 text-sm">
      {STATUS_ICONS[status] ?? <Clock className="w-4 h-4 text-gray-400" />}
      {status}
    </span>
  );
}

function fmtDate(d?: string) {
  if (!d) return '—';
  return new Date(d).toLocaleString();
}

// ─── Create Dialog ────────────────────────────────────────────────

function CreateDialog({
  title,
  fields,
  onSubmit,
  onClose,
}: {
  title: string;
  fields: { key: string; label: string; required?: boolean; type?: string; placeholder?: string }[];
  onSubmit: (values: Record<string, string>) => Promise<void>;
  onClose: () => void;
}) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setErr(null);
    try {
      await onSubmit(values);
      onClose();
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : String(ex));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-background border rounded-lg shadow-lg w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button onClick={onClose} className="p-1 hover:bg-accent rounded"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          {fields.map((f) => (
            <label key={f.key} className="block space-y-1">
              <span className="text-sm font-medium">{f.label}{f.required && ' *'}</span>
              <input
                type={f.type ?? 'text'}
                placeholder={f.placeholder}
                required={f.required}
                value={values[f.key] ?? ''}
                onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                className="w-full border rounded-md px-3 py-2 text-sm bg-background"
              />
            </label>
          ))}
          {err && <p className="text-red-600 text-sm">{err}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-3 py-2 text-sm border rounded-md hover:bg-accent">Cancel</button>
            <button type="submit" disabled={saving} className="px-3 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:opacity-90 disabled:opacity-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> : null}
              Create
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────

export default function DeployControlPage() {
  const [tab, setTab] = useState<Tab>('groups');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionMsg, setActionMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const [groups, setGroups] = useState<Group[]>([]);
  const [instances, setInstances] = useState<Instance[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [runs, setRuns] = useState<Run[]>([]);

  const load = useCallback(async (t: Tab) => {
    setLoading(true);
    setError(null);
    try {
      if (t === 'groups') {
        const data = await apiFetch<Group[]>('/deploy/groups');
        setGroups(data ?? []);
      } else if (t === 'instances') {
        const data = await apiFetch<Instance[]>('/deploy/instances');
        setInstances(data ?? []);
      } else if (t === 'templates') {
        const data = await apiFetch<Template[]>('/deploy/templates');
        setTemplates(data ?? []);
      } else {
        const data = await apiFetch<{ data: Run[]; total: number }>('/deploy/runs?limit=50');
        setRuns(data?.data ?? (data as unknown as Run[]) ?? []);
      }
    } catch {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(tab); }, [tab, load]);

  // ── Write operations ──────────────────────────────────────────

  const createGroup = async (vals: Record<string, string>) => {
    const res = await apiPost('/api/v1/deploy/groups', {
      slug: vals.slug, name: vals.name, description: vals.description,
      region: vals.region || 'ap-southeast-1', domainPattern: vals.domainPattern,
      maxTenants: vals.maxTenants ? Number(vals.maxTenants) : 50,
    });
    if (!res.ok) throw new Error(res.error);
    setActionMsg({ type: 'ok', text: `Group "${vals.name}" created` });
    void load('groups');
  };

  const deleteGroup = async (g: Group) => {
    if ((g.tenant_count ?? 0) > 0) {
      setActionMsg({ type: 'err', text: `Cannot delete: group has ${g.tenant_count} tenants` });
      return;
    }
    const res = await apiDelete(`/api/v1/deploy/groups/${g.id}`);
    if (!res.ok) { setActionMsg({ type: 'err', text: res.error ?? 'Delete failed' }); return; }
    setActionMsg({ type: 'ok', text: `Group "${g.name}" deleted` });
    void load('groups');
  };

  const createInstance = async (vals: Record<string, string>) => {
    const res = await apiPost('/api/v1/deploy/instances', {
      slug: vals.slug, name: vals.name, groupId: vals.groupId,
      domain: vals.domain, railwayProjectId: vals.railwayProjectId,
    });
    if (!res.ok) throw new Error(res.error);
    setActionMsg({ type: 'ok', text: `Instance "${vals.name}" registered` });
    void load('instances');
  };

  const healthCheck = async (inst: Instance) => {
    const res = await apiPost(`/api/v1/deploy/instances/${inst.id}/health`, {
      status: 'healthy', checkedAt: new Date().toISOString(),
    });
    if (!res.ok) { setActionMsg({ type: 'err', text: res.error ?? 'Health check failed' }); return; }
    setActionMsg({ type: 'ok', text: `Health check sent for "${inst.name}"` });
    void load('instances');
  };

  const updateInstanceStatus = async (inst: Instance, status: string) => {
    const res = await apiPatch(`/api/v1/deploy/instances/${inst.id}`, { status });
    if (!res.ok) { setActionMsg({ type: 'err', text: res.error ?? 'Update failed' }); return; }
    setActionMsg({ type: 'ok', text: `Instance "${inst.name}" → ${status}` });
    void load('instances');
  };

  const createTemplate = async (vals: Record<string, string>) => {
    const res = await apiPost('/api/v1/deploy/templates', {
      slug: vals.slug, name: vals.name, description: vals.description,
    });
    if (!res.ok) throw new Error(res.error);
    setActionMsg({ type: 'ok', text: `Template "${vals.name}" created` });
    void load('templates');
  };

  // ── Create dialog field configs ───────────────────────────────

  const createFields: Record<string, { key: string; label: string; required?: boolean; placeholder?: string }[]> = {
    groups: [
      { key: 'slug', label: 'Slug', required: true, placeholder: 'sg-sme' },
      { key: 'name', label: 'Name', required: true, placeholder: 'Singapore SME' },
      { key: 'description', label: 'Description' },
      { key: 'region', label: 'Region', placeholder: 'ap-southeast-1' },
      { key: 'domainPattern', label: 'Domain Pattern', placeholder: '{{tenantSlug}}.sg.boox.app' },
      { key: 'maxTenants', label: 'Max Tenants', placeholder: '50' },
    ],
    instances: [
      { key: 'slug', label: 'Slug', required: true, placeholder: 'sg-prod-01' },
      { key: 'name', label: 'Name', required: true, placeholder: 'Singapore Prod 01' },
      { key: 'groupId', label: 'Group ID', required: true, placeholder: 'UUID of parent group' },
      { key: 'domain', label: 'Domain', placeholder: 'sg-01.boox.app' },
      { key: 'railwayProjectId', label: 'Railway Project ID' },
    ],
    templates: [
      { key: 'slug', label: 'Slug', required: true, placeholder: 'standard-saas' },
      { key: 'name', label: 'Name', required: true, placeholder: 'Standard SaaS' },
      { key: 'description', label: 'Description' },
    ],
  };

  const createHandlers: Record<string, (vals: Record<string, string>) => Promise<void>> = {
    groups: createGroup,
    instances: createInstance,
    templates: createTemplate,
  };

  const tabs: { key: Tab; label: string; icon: React.ReactNode }[] = [
    { key: 'groups',    label: 'Groups',    icon: <Layers className="w-4 h-4" /> },
    { key: 'instances', label: 'Instances', icon: <Server className="w-4 h-4" /> },
    { key: 'templates', label: 'Templates', icon: <FileCode className="w-4 h-4" /> },
    { key: 'runs',      label: 'Runs',      icon: <Play className="w-4 h-4" /> },
  ];

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Deploy Control Plane</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Manage deployment groups, instances, templates, and runs.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {tab !== 'runs' && (
            <button
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-primary text-primary-foreground hover:opacity-90 text-sm"
            >
              <Plus className="w-4 h-4" />
              Create
            </button>
          )}
          <button
            onClick={() => load(tab)}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md border bg-background hover:bg-accent text-sm"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => { setTab(t.key); setShowCreate(false); }}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t.key
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t.icon}
            {t.label}
          </button>
        ))}
      </div>

      {/* Action feedback */}
      {actionMsg && (
        <div className={`flex items-center justify-between rounded p-3 text-sm ${
          actionMsg.type === 'ok' ? 'text-green-700 bg-green-50 border border-green-200' : 'text-red-600 bg-red-50 border border-red-200'
        }`}>
          <span>{actionMsg.text}</span>
          <button onClick={() => setActionMsg(null)} className="ml-2 hover:opacity-70"><X className="w-3 h-3" /></button>
        </div>
      )}

      {error && (
        <div className="text-red-600 bg-red-50 border border-red-200 rounded p-3 text-sm">
          {error}
        </div>
      )}

      {loading && (
        <div className="flex items-center gap-2 text-muted-foreground py-8 justify-center">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading…
        </div>
      )}

      {/* Create dialog */}
      {showCreate && createFields[tab] && createHandlers[tab] && (
        <CreateDialog
          title={`Create ${tab.slice(0, 1).toUpperCase()}${tab.slice(1, -1)}`}
          fields={createFields[tab]}
          onSubmit={createHandlers[tab]}
          onClose={() => setShowCreate(false)}
        />
      )}

      {/* ── Groups ─────────────────────────────────────────────── */}
      {!loading && tab === 'groups' && (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Slug</th>
                <th className="px-4 py-2 text-left font-medium">Name</th>
                <th className="px-4 py-2 text-left font-medium">Region</th>
                <th className="px-4 py-2 text-left font-medium">Tenants</th>
                <th className="px-4 py-2 text-left font-medium">Domain Pattern</th>
                <th className="px-4 py-2 text-left font-medium">Created</th>
                <th className="px-4 py-2 text-left font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {groups.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">No groups configured</td></tr>
              )}
              {groups.map((g) => (
                <tr key={g.id} className="hover:bg-muted/30">
                  <td className="px-4 py-2 font-mono text-xs">{g.slug}</td>
                  <td className="px-4 py-2">{g.name}</td>
                  <td className="px-4 py-2">{g.region ?? '—'}</td>
                  <td className="px-4 py-2">{g.tenant_count ?? 0} / {g.max_tenants ?? '∞'}</td>
                  <td className="px-4 py-2 font-mono text-xs">{g.domain_pattern ?? '—'}</td>
                  <td className="px-4 py-2 text-muted-foreground">{fmtDate(g.created_at)}</td>
                  <td className="px-4 py-2">
                    <button
                      onClick={() => deleteGroup(g)}
                      title="Delete group"
                      className="p-1 rounded hover:bg-red-100 text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Instances ──────────────────────────────────────────── */}
      {!loading && tab === 'instances' && (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Slug</th>
                <th className="px-4 py-2 text-left font-medium">Name</th>
                <th className="px-4 py-2 text-left font-medium">Status</th>
                <th className="px-4 py-2 text-left font-medium">Domain</th>
                <th className="px-4 py-2 text-left font-medium">Last Health Check</th>
                <th className="px-4 py-2 text-left font-medium">Created</th>
                <th className="px-4 py-2 text-left font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {instances.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">No instances registered</td></tr>
              )}
              {instances.map((i) => (
                <tr key={i.id} className="hover:bg-muted/30">
                  <td className="px-4 py-2 font-mono text-xs">{i.slug}</td>
                  <td className="px-4 py-2">{i.name}</td>
                  <td className="px-4 py-2"><StatusBadge status={i.status} /></td>
                  <td className="px-4 py-2 font-mono text-xs">{i.domain ?? '—'}</td>
                  <td className="px-4 py-2 text-muted-foreground">{fmtDate(i.health_checked_at)}</td>
                  <td className="px-4 py-2 text-muted-foreground">{fmtDate(i.created_at)}</td>
                  <td className="px-4 py-2 flex items-center gap-1">
                    <button
                      onClick={() => healthCheck(i)}
                      title="Record health check"
                      className="p-1 rounded hover:bg-green-100 text-green-600 hover:text-green-800"
                    >
                      <Heart className="w-4 h-4" />
                    </button>
                    {i.status !== 'stopped' ? (
                      <button
                        onClick={() => updateInstanceStatus(i, 'stopped')}
                        title="Stop instance"
                        className="p-1 rounded hover:bg-red-100 text-red-500 hover:text-red-700"
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    ) : (
                      <button
                        onClick={() => updateInstanceStatus(i, 'healthy')}
                        title="Start instance"
                        className="p-1 rounded hover:bg-green-100 text-green-600 hover:text-green-800"
                      >
                        <CheckCircle className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Templates ──────────────────────────────────────────── */}
      {!loading && tab === 'templates' && (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Slug</th>
                <th className="px-4 py-2 text-left font-medium">Name</th>
                <th className="px-4 py-2 text-left font-medium">Version</th>
                <th className="px-4 py-2 text-left font-medium">Active</th>
                <th className="px-4 py-2 text-left font-medium">Description</th>
                <th className="px-4 py-2 text-left font-medium">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {templates.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No templates defined</td></tr>
              )}
              {templates.map((t) => (
                <tr key={t.id} className="hover:bg-muted/30">
                  <td className="px-4 py-2 font-mono text-xs">{t.slug}</td>
                  <td className="px-4 py-2">{t.name}</td>
                  <td className="px-4 py-2">v{t.version}</td>
                  <td className="px-4 py-2">
                    {t.is_active
                      ? <CheckCircle className="w-4 h-4 text-green-500" />
                      : <XCircle className="w-4 h-4 text-gray-400" />}
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">{t.description ?? '—'}</td>
                  <td className="px-4 py-2 text-muted-foreground">{fmtDate(t.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Runs ───────────────────────────────────────────────── */}
      {!loading && tab === 'runs' && (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Action</th>
                <th className="px-4 py-2 text-left font-medium">Status</th>
                <th className="px-4 py-2 text-left font-medium">Instance</th>
                <th className="px-4 py-2 text-left font-medium">Started</th>
                <th className="px-4 py-2 text-left font-medium">Finished</th>
                <th className="px-4 py-2 text-left font-medium">Error</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {runs.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No deployment runs recorded</td></tr>
              )}
              {runs.map((r) => (
                <tr key={r.id} className="hover:bg-muted/30">
                  <td className="px-4 py-2 font-mono text-xs">{r.action}</td>
                  <td className="px-4 py-2"><StatusBadge status={r.status} /></td>
                  <td className="px-4 py-2 font-mono text-xs">{r.instance_id.slice(0, 8)}…</td>
                  <td className="px-4 py-2 text-muted-foreground">{fmtDate(r.started_at)}</td>
                  <td className="px-4 py-2 text-muted-foreground">{fmtDate(r.finished_at)}</td>
                  <td className="px-4 py-2 text-red-500 text-xs">{r.error_message ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
